import PropTypes from "prop-types"
import { get, size, map } from "lodash"
import { useSelector } from "react-redux"

import classNames from "classnames"
import { Link, Outlet } from "react-router-dom"
import { AppBar, Menu, Paper } from "@mui/material"
import { AccountCircle, FilterNone, Star, People, Person, ExitToApp, SyncAlt, FilterDrama, Settings, PhotoSizeSelectActual, RotateLeft, Webhook, SpaceDashboardOutlined, AttachFile } from "@mui/icons-material"
import Search from "modules/home-search"
import Notifications from "modules/notifications"
import ServerNotifications from "modules/server-notifications"
import StaticAlert from "modules/static-alert"
import FactorProfile from "modules/factor-profile"
import SecurityProfile from "modules/security-profile"
import HelpMenu from "modules/help-menu"
import ThemeModeToggle from "components/theme-mode-toggle"
import Dialog from "modules/dialog"
import { ROLE } from "helpers/shapes"
import Modal from "components/modal"
import Logo from "components/logo"
import { useIsDefinedRouterMatchProp, useLastRouterMatchProp } from "hooks/router"
import Item from "./components/item"
import SettingsMenuItem from "./components/settings-menu-item"
import ResourceTooltipInfo from "components/resource-tooltip-info"
import SearchButton from "./components/search-button"
import { getIsDashboardEnabled } from "selectors/features"

import "./style.scss"

export default function Home({
  header,
  account,
  accountId,
  settingsMenuAnchor,
  isSuperuser,
  isSuperuserSession,
  role,
  multiAccounts,
  security,
  model,
  models,
  setModel,
  closeSecurityProfile,
  factorId,
  closeFactorProfile,
  setHeader,
  sessionExpired,
  openDialog,
  trackSearchIcon,
  trackMenu,
  toggleSettingsMenu,
  hasSolutions,
}) {
  const HeaderElem = useLastRouterMatchProp("header")
  const HeaderIcon = useLastRouterMatchProp("headerIcon")
  const headerTitle = useLastRouterMatchProp("headerTitle")
  const headerActions = useLastRouterMatchProp("headerActions")
  const noSideMenu = useLastRouterMatchProp("noSideMenu", false)

  const noModelSelector = useIsDefinedRouterMatchProp("noModelSelector")

  // [TODO] does this need memoizing?
  const openCompareSearch = ev => {
    trackSearchIcon()
    ev.currentTarget.blur()
    openDialog({
      content: <Search modelId={get(header, "modelId")} disableModel={noModelSelector} />,
    })
  }

  // [TODO] does this need memoizing?
  const getTrackMenu = (type) => {
    return () => {
      trackMenu(type)
    }
  }

  // [TODO] does this need memoizing?
  const toggleSettingsMenuHandler = (ev) => {
    toggleSettingsMenu(!settingsMenuAnchor ? ev.currentTarget : null)
  }

  // The header prop is not accessible from the selectors
  const selectedModelId = get(header, "modelId") || model

  const isDashboardEnabled = useSelector(getIsDashboardEnabled)

  return <div className={classNames("modules_home-layout", { "subnav-enabled": !!header.subnav, "no-shadow": header.noShadow })}>
    {HeaderElem && <HeaderElem account={account} />}
    {headerTitle && <AppBar position="sticky">
      <div className="header-app-bar">
        <div className="left">
          <Link className="logo" to={`/${account}/home`}>
            <Logo />
          </Link>
          {HeaderIcon && <HeaderIcon />}
          <span className="title">{headerTitle}</span>
        </div>
        <div className="right">
          {headerActions && <div className="header-app-actions">
            {map(headerActions, (Action, index) =>
              <div key={`rightAction-${index}`} className="header-app-action">
                <Action />
              </div>,
            )}
          </div>}
          <ServerNotifications homeVersion />
          <HelpMenu iconButton />
          <ThemeModeToggle inline />
        </div>
      </div>
    </AppBar>}
    {!HeaderElem && !headerTitle && <AppBar elevation={header.noShadow ? 0 : 4} position="sticky">
      <div className="header-app-bar">
        <div className="left">
          {header.actions && header.actions.home
            ? <div className="logo" onClick={header.actions.home}><Logo /></div>
            : <Link className="logo" to={`/${account}/home`}>
              <Logo />
            </Link>}
          {header.menu}
          {header.icon && <header.icon />}
          {header.title && <span className="title">{header.title}</span>}
          {header.info && header.info.length > 0
            && <div className="info-tooltip">
              <ResourceTooltipInfo info={header.info} />
            </div>}
        </div>
        <div className="center">
          {header.topnav && <span className="topnav">{header.topnav}</span>}
        </div>
        <div className="right">
          {header.actions && header.actions.rightActions && <div className="header-app-actions">
            {map(header.actions.rightActions, (action, index) =>
              <div key={`rightAction-${index}`} className="header-app-action">
                {action}
              </div>)}
          </div>}
          <ServerNotifications homeVersion />
          {get(header, "actions.help") ? header.actions.help : <HelpMenu iconButton />}
          <ThemeModeToggle inline />
        </div>
      </div>
      {header.subnav && <div className="header-subnav">
        {header.subnav}
      </div>}
    </AppBar>}
    <div className="bottom">
      {!noSideMenu && <Paper square className="nav-bar">
        <div className="top">
          <SearchButton noModels={!size(models)} onClick={openCompareSearch} />
          {isDashboardEnabled && <Item label="Dashboard" MaterialIcon={SpaceDashboardOutlined} to="dashboard" onClick={getTrackMenu("dashboard")} showBetaTag />}
          <Item label="Market" MaterialIcon={PhotoSizeSelectActual} to="home/market" onClick={getTrackMenu("market-tools")} disabled={!size(models)} disabledTooltip="Market Tools is disabled since you do not have access to any risk model" />
          <Item label="Favorites" MaterialIcon={Star} to="home/favorites" onClick={getTrackMenu("favorites")} />
          <Item label="Resources" MaterialIcon={FilterNone} to="home/resources" onClick={getTrackMenu("resources")} />
          <Item label="Data" MaterialIcon={FilterDrama} to="home/data-cloud" onClick={getTrackMenu("data-cloud")} />
          {(isSuperuser || role === "admin") && <Item label="Platform" MaterialIcon={Webhook} to="home/platform" onClick={getTrackMenu("platform")} />}
          {(isSuperuser || hasSolutions) && <Item label="Solutions" MaterialIcon={AttachFile} to="home/solutions" onClick={getTrackMenu("solutions")} />}
        </div>
        <div className="bottom">
          <Item label="Account" className="settings-menu-button" onClick={toggleSettingsMenuHandler} MaterialIcon={AccountCircle} />
        </div>
      </Paper>}
      <div className="content">
        <StaticAlert />
        <Outlet context={{ setHeader, appHeaderKey: header.key }} />
        <Notifications />
      </div>
    </div>
    <Dialog />
    {security && <Modal superModalOpened={sessionExpired} onClose={closeSecurityProfile} className="modules_security-profile" noButton>
      <SecurityProfile security={security} modelId={selectedModelId} models={models} setModel={!noModelSelector ? setModel : null} onClose={closeSecurityProfile} />
    </Modal>}
    {factorId && <Modal superModalOpened={sessionExpired} onClose={closeFactorProfile} className="modules_factor-profile" noButton>
      <FactorProfile factorId={factorId} modelId={selectedModelId} onClose={closeFactorProfile} />
    </Modal>}
    <Menu
      anchorEl={settingsMenuAnchor}
      open={!!settingsMenuAnchor}
      onClose={toggleSettingsMenuHandler}
      classes={{ paper: "home-settings-menu" }}
      style={{ zIndex: 20000 }}
    >
      {(role === "admin") && <SettingsMenuItem className="account-settings-item" label="Account Settings" Icon={Settings} to="home/account" closeMenu={toggleSettingsMenuHandler} />}
      <SettingsMenuItem className="members-item" activeUrls={["teams", "members"]} label="Teams & Members" Icon={People} to="home/members" closeMenu={toggleSettingsMenuHandler} />
      <SettingsMenuItem label="My Profile" className="my-profile-item" Icon={Person} to="home/profile" closeMenu={toggleSettingsMenuHandler} />
      {isSuperuser && <SettingsMenuItem className="admin-item" label="Admin" Icon={SyncAlt} to="/admin" closeMenu={toggleSettingsMenuHandler} />}
      {multiAccounts && <SettingsMenuItem className="accounts-item" label="Accounts" Icon={SyncAlt} to="/accounts" closeMenu={toggleSettingsMenuHandler} />}
      {isSuperuserSession && <SettingsMenuItem className="restore-session" label="Reset Session" Icon={RotateLeft} to={`/resetSession?accountId=${accountId}&redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`} closeMenu={toggleSettingsMenuHandler} />}
      <SettingsMenuItem className="logout-item" label="Logout" Icon={ExitToApp} to="/logout" closeMenu={toggleSettingsMenuHandler} />
    </Menu>
  </div>
}

Home.propTypes = {
  hasApiAccess: PropTypes.bool.isRequired,
  isSuperuser: PropTypes.bool.isRequired,
  isSuperuserSession: PropTypes.bool.isRequired,
  role: ROLE.isRequired,
  multiAccounts: PropTypes.bool.isRequired,
  model: PropTypes.string,
  setModel: PropTypes.func.isRequired,
  models: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })).isRequired,
  security: PropTypes.object,
  account: PropTypes.string.isRequired,
  accountId: PropTypes.string.isRequired,
  openDialog: PropTypes.func.isRequired,
  trackSearchIcon: PropTypes.func.isRequired,
  toggleSettingsMenu: PropTypes.func.isRequired,
  closeSecurityProfile: PropTypes.func.isRequired,
  trackMenu: PropTypes.func.isRequired,
  setHeader: PropTypes.func.isRequired,
  settingsMenuAnchor: PropTypes.object,
  header: PropTypes.object.isRequired,
  closeFactorProfile: PropTypes.func.isRequired,
  factorId: PropTypes.string,
  sessionExpired: PropTypes.bool,
  hasSolutions: PropTypes.bool.isRequired,
}
